import {
  Component,
  Position
} from "puzzle";
import {
  GS_BUILDUP_PROPS, MASS_SELECTION_CONFIGURATION,
  PROPS,
  TARGET
} from "../../Types";
import { get, some } from "lodash";

type GS_PROPS = Readonly<PROPS> & Readonly<{ children?: import("react").ReactNode }>;
type GS_TARGET = TARGET & {
  targetLanguageName: string | undefined
};
type OPEN_GS_PARAM = {
  data: GS_TARGET[]
  files: { uploaded: any[] }
  massSelectionConfiguration: MASS_SELECTION_CONFIGURATION
  total: number
};

class GradingAndScoring extends Component {
  /**
   * Returns gsEndpoint endpoint
   * @param props
   */
  getEndpoint = (props: GS_PROPS | GS_BUILDUP_PROPS): string => {
    const { isSupplierView } = props;
    return isSupplierView ? `/jobPhases/${props.submissionData.taskId}/displayGradingAndScoring` : '/submissions/displayGradingAndScoring';
  };

  /**
   * Checks if all selected files are on same phase,batch and language.
   *
   * @param selectedRecords
   * @param props
   */
  sameBatchPhaseLanguage = (selectedRecords: GS_TARGET[], props: GS_PROPS | GS_BUILDUP_PROPS): boolean => {
    const { isSupplierView } = props;
    let {
      batchName,
      phaseName,
      status,
      targetLanguage,
      targetLanguageName
    } = selectedRecords[0];
    let flag = status?.name !== 'CLAIMED';//if at least one file has CLAIMED status (in this case first) method will return false
    let i = 1;
    while (i < selectedRecords.length && flag) {
      if (!isSupplierView) {
        flag = batchName === selectedRecords[i].batchName;
        if (targetLanguageName) { //report dialog does not have targetLanguage.locale in its data, so we use targetLanguageName
          flag = flag && (targetLanguageName === selectedRecords[i].targetLanguageName);
        } else {
          flag = flag && (targetLanguage.locale === selectedRecords[i].targetLanguage.locale);
        }
        flag = flag && (phaseName === selectedRecords[i].phaseName);
      } else {
        const { name: statusName } = selectedRecords[i].status;
        if (statusName === 'CLAIMED') {
          flag = false;
        } else {
          const completed = "COMPLETED";
          flag = status.name === completed ? statusName === completed : statusName !== completed;
        }
      }
      i++;
    }

    return flag;
  };

  /**
   * Show warning when user tries to grade and score on phase where gs in not available (notRequired ss flag)
   * @param selectedRecords
   * @param props
   */
  hasNotAvailableGS = (selectedRecords: TARGET[], props: GS_PROPS | GS_BUILDUP_PROPS): boolean => {
    let flag = false;
    let i = 0;
    if (!props.isSupplierView) {
      while (i < selectedRecords.length && !flag) {
        let { workflowConfiguration, status } = selectedRecords[i];
        if (workflowConfiguration) {
          flag = workflowConfiguration.gradingAndScoring === 'notRequired';
        }
        if (!flag) {
          flag = ["IN_ERROR", "IN_GATE"].includes(status?.name);
        }
        i++;
      }
    }

    return flag;
  };

  /**
   * Returns true if all at least one selected file is not claimed. False otherwise
   *
   * @param selectedRecords
   */
  hasNotClaimedFiles = (selectedRecords: TARGET[]) => some(selectedRecords, {
    status: { name: 'READY' }
  });

  /**
   * Opens g+s dialog or proper warning dialog triggered by validation
   * @param state
   * @param props
   * @param all
   */
  openGradingAndScoring = (state: OPEN_GS_PARAM, props: GS_PROPS | GS_BUILDUP_PROPS, all: boolean): void => {
    const {
      data = undefined,
      files = undefined,//report dialog data
      total = 0
    } = state;
    const limit = 200;

    let selectedRecords: GS_TARGET[] = [];
    if (data?.length) {
      selectedRecords = data.filter((item: TARGET) => ((all || item.selected) && item.status?.name !== 'CANCELLED'));
    } else if (files?.uploaded?.length) {
      selectedRecords = files.uploaded;
    }

    if (selectedRecords.length > limit || (all && total > limit)) {
      this.dispatch('gradingAndScoringInvalidSelection', Position.end, { limit });
    } else if (!this.sameBatchPhaseLanguage(selectedRecords, props)) {
      this.dispatch('notSameBatchPhaseAndLanguageWarning', Position.end);
    } else if (data?.length && !props?.isSupplierView && this.hasNotAvailableGS(selectedRecords, props)) {
      this.dispatch('notConfiguredGradingAndScoring', Position.end); // todo g+s can be optimised for just one iteration through targets
    } else if (!props?.isSupplierView && this.hasNotClaimedFiles(selectedRecords)) {
      this.dispatch('claimNotClaimedFilesWarning', '', {
        actionText: this.translate('gradeAndScore'),
        all
      })
    } else {
      let {
        batchName,
        targetLanguage,
        targetLanguageName
      } = selectedRecords[0];
      const targetLanguageDisplayName = targetLanguage?.value || targetLanguageName;
      let {
        phaseType = undefined,
        phaseName = undefined,
        projectId,
        submissionId,
        submissionName
      } = props.submissionData;
      let supplierCompletedFiles = false;

      if (phaseName) {
        supplierCompletedFiles = selectedRecords.every((record: TARGET) => record.status.name === "COMPLETED");
      } else {
        phaseName = selectedRecords[0].phaseName;
      }

      if (!phaseType) {
        phaseType = get(selectedRecords, '[0].phaseType') || state?.massSelectionConfiguration?.phaseType;
      }
      this.dispatch('openGradingAndScoring', Position.end, {
        endpoint: this.getEndpoint(props),
        batchName,
        submissionName,
        supplierCompletedFiles,
        targetLanguageDisplayName,
        filesCount: selectedRecords.length,
        gradeAndScoreRequestParams: {
          phaseName,
          phaseType,
          projectId,
          submissionId,
          targetIds: selectedRecords.reduce((acc: string[], item: TARGET) => {
            if (item.targetId && (!item.status || !['CANCELLED', 'CLAIMED'].includes(item.status.name))) { //part when there is no statuses is from report dialog
              acc.push(item.targetId);
            }
            return acc;
          }, [])
        }
      });
    }
  };
}

export default GradingAndScoring;
